import React from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styles from './ChatMessage.module.scss';
import Sources from './Sources';
import MessageStatusComponent from './MessageStatus';
import { ClientMessage } from '../../../../../../../store/chat/types';
import { Text } from '../../../../../../../components/Typography/Typography';
import { ChatRole } from '../../../../../../../types/ChatRoles';
import { ChatMessageType } from '../../../../../../../store/chat/types';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeReact from 'rehype-react';
import { visit } from 'unist-util-visit';

interface ChatMessageProps {
  message: ClientMessage;
  showStatus?: boolean;
}

const normalizeText = (text: string): string => {
  // Replace 3 or more newlines with 1 newline
  return text.replace(/\n{3,}/g, '\n');
};

function citationInjector(citationElement: React.ReactNode) {
  return () => (tree: any) => {
    let lastNode: any = null;

    visit(tree, (node) => {
      if (node.type === 'paragraph' || node.type === 'listItem') {
        lastNode = node;
      }
    });

    if (lastNode) {
      // Push a custom inline node (citations)
      lastNode.children.push({
        type: 'jsx',
        value: citationElement,
      });
    }
  };
}

function renderMarkdownWithCitations(markdown: string, citations: React.ReactNode) {
  return unified()
    .use(remarkParse)
    .use(citationInjector(citations))
    .use(remarkRehype, { allowDangerousHtml: true }) // needed to allow jsx inline
    .use(rehypeReact, {
      jsx,
      jsxs,
      Fragment,
    })
    .processSync(markdown).result as React.ReactNode;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, showStatus = false }) => {
  const { blocks, role, timestamp, sources, type } = message;

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isThinkingMessage = type === ChatMessageType.Thinking;
  const contentClass =
    `${styles.messageContent} ${isThinkingMessage ? styles.thinkingMessage : ''}`.trim();

  let citationsOffset = 0;

  return (
    <div className={`${styles.messageRow} ${styles[role]}`}>
      <div className={styles.messageContainer}>
        <div className={contentClass}>
          {!isThinkingMessage && (
            <Text size="small" weight="regular" className={styles.messageSender}>
              {role === ChatRole.User ? 'You' : 'Enlo'}
            </Text>
          )}

          <div className={styles.messageText}>
            {blocks.map((block, idx) => {
              const offset = citationsOffset;
              citationsOffset += block.sources ? block.sources.length : 0;

              const citations =
                block.sources && block.sources.length > 0 ? (
                  <Sources sources={block.sources} inline indexOffset={offset} />
                ) : null;
              const markdownContent = renderMarkdownWithCitations(
                normalizeText(block.content || ''),
                citations,
              );
              return (
                <div key={idx} className={styles.messageBlock}>
                  {markdownContent}
                </div>
              );
            })}
          </div>

          {sources && sources.length > 0 && (
            <Sources sources={sources} indexOffset={citationsOffset} />
          )}

          {!isThinkingMessage && (
            <Text size="small" weight="regular" color="#6B7271" className={styles.messageTime}>
              {formatTime(timestamp)}
            </Text>
          )}
        </div>
        {showStatus && message.status && <MessageStatusComponent status={message.status} />}
      </div>
    </div>
  );
};

export default ChatMessage;
